import { MessagePrimitive, MessageProvider, useAui } from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import ChatLoadingActivity from 'components/Chat/ChatLoadingActivity';
import { ChatMarkdownLink, ChatMarkdownTable, getChatMarkdownStyles } from 'components/Chat/ChatMarkdown';
import ChatPromptDispatcher from 'components/Chat/ChatPromptDispatcher';
import useChatMessagesViewModel from 'hooks/chat/useChatMessagesViewModel';
import { Trans, useTranslation } from 'react-i18next';
import remarkGfm from 'remark-gfm';
import { Box, Icon, IconButton, Stack, styled, Tag, Text } from 'shell/components';
import { AssistantInteractionStatus, ChatLayout, ChatPromptInputType, ChatRole } from 'utils/chat/chatConstants';
import { preserveMarkdownLineBreaks } from 'utils/chat/chatHelpers';
import { useChatRuntime } from './AssistantChatRuntime';

const AssistantMessageMarkdown = ({ fallbackText, isStreaming, message, messageIndex }) => {
    if (messageIndex < 0) {
        return (
            <Text color={'inherit'} sx={{ whiteSpace: 'pre-wrap' }}>
                {fallbackText}
            </Text>
        );
    }

    return (
        <MessageProvider index={messageIndex} isLast={isStreaming} message={message}>
            <MessagePrimitive.Parts>
                {({ part }) => {
                    if (part.type !== 'text') return null;

                    return (
                        <StyledMarkdownTextPrimitive
                            components={{ a: ChatMarkdownLink, table: ChatMarkdownTable }}
                            preprocess={preserveMarkdownLineBreaks}
                            remarkPlugins={[remarkGfm]}
                            smooth={isStreaming}
                        />
                    );
                }}
            </MessagePrimitive.Parts>
        </MessageProvider>
    );
};

const ChatMessages = ({
    assistantIdentity = null,
    hasLoadingPlaceholder,
    isRunning,
    loadingActivity = null,
    layout = ChatLayout.DEFAULT,
    messages,
}) => {
    const aui = useAui();
    const chatRuntime = useChatRuntime();
    const { t } = useTranslation('assignment');
    const { contentRef, copyAssistantMessage, timeline } = useChatMessagesViewModel({
        isRunning,
        messages,
    });

    const renderSystemMessage = (item) => (
        <SystemMessageRow key={item.id} alignItems={'center'} direction={'row'} spacing={1}>
            {typeof item.draftNumber === 'number' ? (
                <Text color={'light'} component={'span'} variant={'p3'}>
                    <Trans
                        i18nKey={'chat:submission.submission-created-rich-with-time'}
                        defaults={'<draft/> has been submitted at {{time}}'}
                        values={{ time: item.timeLabel }}
                        components={{
                            draft: <ChatDraftTag component={'span'} type={'draft'} value={item.draftNumber} />,
                        }}
                    />
                </Text>
            ) : (
                <Text color={'light'} variant={'p3'}>
                    {item.text}
                </Text>
            )}
        </SystemMessageRow>
    );

    const renderMessageMeta = (item) => {
        if (!item.shouldShowMessageMeta) return null;

        return (
            <MessageMetaRow role={item.role} direction={'row'} spacing={0.5}>
                <MessageTimestamp color={'light'} role={item.role} variant={'p3'}>
                    {item.timeLabel}
                </MessageTimestamp>
                {item.role === ChatRole.ASSISTANT && (
                    <CopyButton
                        aria-label={'Copy assistant response'}
                        kind={'copy'}
                        size={'small'}
                        variant={'square'}
                        onClick={() =>
                            copyAssistantMessage({
                                fallbackText: item.text,
                                interactionResponse: item.interactionResponse,
                                messageId: item.id,
                                requestedInteraction: item.requestedInteraction,
                            })
                        }
                    />
                )}
            </MessageMetaRow>
        );
    };

    const sendInteractionResponse = ({ item, messageText, response }) => {
        if (isRunning || item.interactionResponse) return;

        if (item.requestedInteraction?.input_type === ChatPromptInputType.CONTINUE) {
            chatRuntime?.continueAssistantMessage?.({
                requestedInteraction: item.requestedInteraction,
                sourceMessageId: item.id,
            });
            return;
        }

        aui.thread().append({
            role: ChatRole.USER,
            content: [{ type: 'text', text: messageText }],
            metadata: {
                custom: {
                    interaction_response: {
                        source_message_id: item.id,
                        requested_interaction_id: item.requestedInteraction.id,
                        ...response,
                        input_type: item.requestedInteraction.input_type || ChatPromptInputType.BINARY,
                    },
                },
            },
        });
    };

    const renderRequestedInteraction = (item) => {
        if (!item.requestedInteraction || item.isStreamingAssistantMessage) return null;
        if (!item.isRequestedInteractionCurrent && !item.interactionResponse) return null;
        if (
            item.requestedInteraction.input_type === ChatPromptInputType.CONTINUE &&
            item.requestedInteraction.status &&
            item.requestedInteraction.status !== AssistantInteractionStatus.PENDING
        ) {
            return null;
        }

        return (
            <ChatPromptDispatcher
                disabled={isRunning || Boolean(item.interactionResponse)}
                prompt={item.requestedInteraction}
                response={item.interactionResponse}
                onSubmit={({ messageText, response }) => sendInteractionResponse({ item, messageText, response })}
            />
        );
    };

    const renderAssistantIdentity = () => {
        if (!assistantIdentity) return null;

        return (
            <Stack sx={{ mb: 1 }} alignItems={'center'} direction={'row'} spacing={2}>
                {assistantIdentity.icon ? (
                    <AssistantIconBadge>
                        <Icon kind={assistantIdentity.icon} />
                    </AssistantIconBadge>
                ) : null}
                {assistantIdentity.label ? <Text variant={'p2'}>{assistantIdentity.label}</Text> : null}
            </Stack>
        );
    };

    const renderChatMessage = (item) => (
        <MessageRow key={item.id} role={item.role} spacing={1}>
            {item.role === ChatRole.ASSISTANT ? renderAssistantIdentity() : null}
            <MessageCopyContent data-message-copy-id={item.id}>
                {item.text.trim() ? (
                    <MessageBubble layout={layout} role={item.role}>
                        {item.role === ChatRole.ASSISTANT ? (
                            <AssistantMessageContent data-message-content-id={item.id}>
                                <AssistantMessageMarkdown
                                    fallbackText={item.text}
                                    isStreaming={item.isStreamingAssistantMessage}
                                    message={item.message}
                                    messageIndex={item.messageIndex}
                                />
                            </AssistantMessageContent>
                        ) : (
                            <Text color={'inherit'} sx={{ whiteSpace: 'pre-wrap' }}>
                                {item.text}
                            </Text>
                        )}
                    </MessageBubble>
                ) : null}
                {item.role === ChatRole.ASSISTANT ? renderRequestedInteraction(item) : null}
            </MessageCopyContent>
            {renderMessageMeta(item)}
        </MessageRow>
    );

    const renderLoadingState = () => {
        const hasLoadingActivity = loadingActivity?.getActivity || loadingActivity?.statuses?.length;
        if (!hasLoadingPlaceholder && !hasLoadingActivity) return null;

        if (hasLoadingActivity) {
            return (
                <Fade
                    in={hasLoadingPlaceholder}
                    mountOnEnter={true}
                    timeout={{ enter: 220, exit: 420 }}
                    unmountOnExit={true}
                >
                    <Collapse in={hasLoadingPlaceholder} timeout={{ enter: 240, exit: 420 }} unmountOnExit={true}>
                        <Box>
                            <ChatLoadingActivity {...loadingActivity} />
                        </Box>
                    </Collapse>
                </Fade>
            );
        }

        return (
            <LoadingMessageRow spacing={1}>
                <LoadingMessageText variant={'p3'}>{t('chat:thinking', 'Thinking...')}</LoadingMessageText>
            </LoadingMessageRow>
        );
    };

    return (
        <MessagesStack ref={contentRef} spacing={5}>
            {timeline.map((group) => (
                <Stack key={group.dateKey} spacing={5}>
                    <DateDivider>
                        <Text color={'light'} variant={'p3'}>
                            {t('common:ui.format-date-long', { val: group.date })}
                        </Text>
                    </DateDivider>

                    {group.items.map((item) => (
                        <Stack key={item.id} spacing={5}>
                            {item.role === ChatRole.SYSTEM ? renderSystemMessage(item) : renderChatMessage(item)}
                        </Stack>
                    ))}
                </Stack>
            ))}

            {renderLoadingState()}
        </MessagesStack>
    );
};

const StyledMarkdownTextPrimitive = styled(MarkdownTextPrimitive)(({ theme }) => getChatMarkdownStyles({ theme }));

const MessagesStack = styled(Stack)(({ theme }) => {
    return {
        minHeight: 0,
        flexShrink: 0,

        '& > *:first-child': {
            paddingTop: theme.spacing(4),
        },
        '& > *:last-child': {
            paddingBottom: '16px',
        },
    };
});

const DateDivider = styled(Stack)(() => {
    return {
        alignItems: 'center',
        textAlign: 'center',
    };
});

const SystemMessageRow = styled(Stack)(({ theme }) => {
    return {
        paddingBottom: theme.spacing(1),
    };
});

const ChatDraftTag = styled(Tag)(({ theme }) => {
    return {
        background: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.neutral[800],
        height: 'auto',
    };
});

const MessageRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        alignItems: role === ChatRole.USER ? 'flex-end' : 'flex-start',
    };
});

const AssistantIconBadge = styled(Box)(({ theme }) => {
    return {
        display: 'inline-flex',
        flexShrink: 0,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        background: theme.palette.ensightful.warning[50],
        color: theme.palette.ensightful.warning[800],
        height: '40px',
        width: '40px',

        '& > svg': {
            fontSize: '30px',
        },
    };
});

const MessageBubbleMaxWidthByLayout = {
    [ChatLayout.DEFAULT]: {
        [ChatRole.USER]: '85%',
        [ChatRole.ASSISTANT]: '90%',
    },
    [ChatLayout.FULLSCREEN]: {
        [ChatRole.USER]: 'min(85%, 720px)',
        [ChatRole.ASSISTANT]: 'min(90%, 860px)',
    },
};

const getMessageBubbleMaxWidth = ({ layout, role }) => {
    const layoutConfig = MessageBubbleMaxWidthByLayout[layout] ?? MessageBubbleMaxWidthByLayout[ChatLayout.DEFAULT];

    return layoutConfig?.[role] ?? '90%';
};

const MessageBubble = styled(Stack, {
    shouldForwardProp: (prop) => !['layout', 'role'].includes(prop),
})(({ theme, layout, role }) => {
    return {
        maxWidth: getMessageBubbleMaxWidth({ layout, role }),
        lineHeight: '20px',
        borderRadius: '16px',
        padding: role === ChatRole.USER ? theme.spacing(2, 4) : theme.spacing(4),
        background: role === ChatRole.USER ? theme.palette.ensightful.neutral[100] : '#F2EFE9',
        color: theme.palette.ensightful.neutral[900],
    };
});

const AssistantMessageContent = styled('div')(() => {
    return {
        width: '100%',
    };
});

const MessageCopyContent = styled('div')(() => {
    return {
        display: 'contents',
    };
});

const MessageMetaRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        alignItems: 'center',
        justifyContent: role === ChatRole.USER ? 'flex-end' : 'flex-start',
    };
});

const MessageTimestamp = styled(Text, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        textAlign: role === ChatRole.USER ? 'right' : 'left',
    };
});

const CopyButton = styled(IconButton)(() => {
    return {
        minWidth: 0,
    };
});

const LoadingMessageRow = styled(Stack)(() => {
    return {
        alignItems: 'flex-start',
    };
});

const LoadingMessageText = styled(Text)(({ theme }) => {
    return {
        background: `linear-gradient(90deg, 
            ${theme.palette.ensightful.neutral[800]} 0%, 
            ${theme.palette.ensightful.neutral[400]} 50%, 
            ${theme.palette.ensightful.neutral[800]} 100%
        )`,
        backgroundSize: '180px 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
        animation: 'chat-loading-gradient 2s linear infinite',

        '@keyframes chat-loading-gradient': {
            '0%': {
                backgroundPosition: '0px center',
            },
            '100%': {
                backgroundPosition: '180px center',
            },
        },
    };
});

export default ChatMessages;
